import React, { useState } from 'react';
import styled from 'styled-components';

import { InputText, Spinner, Modal, Text, Button } from '@companycam/slab-web';

import { translate } from 'javascripts/translations/i18n';
import { useEditReviewProfile } from 'data/v3/reviewProfiles';
import { useReviewProfiles } from './hooks/useReviewProfiles';
import { useModal } from 'components/shared/hooks';
import toast from 'helpers/toast';
import { Steven } from 'helpers/steven';

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledModal = styled(Modal)``;

export type EditReviewProfileNicknameModalProps = {
  reviewProfileId: number;
};

const EditReviewProfileNicknameModal = ({
  reviewProfileId,
}: EditReviewProfileNicknameModalProps) => {
  const { reviewProfiles, isReviewProfilesLoading, refetchReviewProfiles } =
    useReviewProfiles();
  const reviewProfile = reviewProfiles.find(
    (profile) => profile.id === reviewProfileId,
  );
  const [nickname, setNickname] = useState(reviewProfile?.nickname || '');
  const { hideModal } = useModal();
  const profileNotFound = !reviewProfile && !isReviewProfilesLoading;

  const handleError = () => {
    hideModal();
    toast.error(translate('project.reviews.reviewsEdit.nicknameUpdatedError'));
  };

  const handleSuccess = () => {
    refetchReviewProfiles();
    toast.success(
      translate('project.reviews.reviewsEdit.nicknameUpdatedSuccess'),
    );
    Steven.entityCrud({
      entity_type: 'review_profile',
      entity_id: reviewProfileId.toString(),
      action: 'updated',
    });
    hideModal();
  };

  const reviewProfileMutation = useEditReviewProfile({
    reviewProfileId,
    onError: handleError,
    onSuccess: handleSuccess,
  });

  const handleSubmit = () => {
    const updatedProfile = {
      ...reviewProfile,
      nickname,
    };
    reviewProfileMutation.mutate(updatedProfile);
  };

  if (isReviewProfilesLoading) {
    return (
      <SpinnerContainer>
        <Spinner
          accessibilityLabel={translate('common.loading')}
          size="large"
        />
      </SpinnerContainer>
    );
  }

  if (profileNotFound) {
    hideModal();
    toast.error(translate('project.reviews.reviewsEdit.profileNotFound'));
  }

  return (
    <StyledModal
      title={null}
      headerComponent={null}
      size="small"
      isOpen
      onClose={hideModal}
      footerActions={
        <>
          <Button onClick={hideModal}>{translate('common.cancel')}</Button>
          <Button
            color="primary"
            disabled={nickname === ''}
            onClick={handleSubmit}
          >
            {translate('common.save')}
          </Button>
        </>
      }
    >
      <Text as="h2" size="xl" ccMargin="none none m none">
        {translate('project.reviews.reviewsEdit.editNickname')}
      </Text>
      <InputText
        id="edit-public-profile-nickname"
        label={null}
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
      />
    </StyledModal>
  );
};

export default EditReviewProfileNicknameModal;
